import {applyPublicConfig, analyticsEnabled} from 'utils/deployment';
import 'promise-polyfill/src/polyfill';

import React, {Component} from 'react';
import {connect} from "react-redux";
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';

import {TouchBackend} from 'react-dnd-touch-backend';
import {DndProvider} from 'react-dnd';

import * as Sentry from "@sentry/browser";

import setLang from 'utils/i18n';
import fetchRequest from 'utils/request';
import {setLocale} from 'utils/time';
import asyncComponent from 'utils/asyncLoadComponentHOC';
import {
  finishUpdateAppInfo,
  startUpdateAppInfo,
  updateAppInfo,
  updateUser
} from 'reduxBase/appInfo/actions';
import {themeColorChanger, themeColorGetter} from 'components/ColorThemePicker/changer';
import {modeColorChanger, modeColorGetter} from 'components/ColorMode/changer';

import querystring from 'query-string';

const pages = {
  staff: {
    fileName: 'staff'
  },
  entrance: {
    fileName: 'entrance'
  },
  app: {
    fileName: 'app'
  },
  setup: {
    fileName: 'setup'
  },
  invitation: {
    fileName: 'invitation'
  },
  onboarding: {
    fileName: 'onboarding'
  },
  setupEmail: {
    fileName: 'setupEmail'
  },
  confirmEmail: {
    fileName: 'confirmEmail'
  },
};

const eventsOptions = {
  enableMouseEvents: true,
  enableTouchEvents: true
}

class IndexRouter extends Component {
  static displayName = 'IndexRouter';

  constructor(props) {
    super(props);
    this.state = {
      page: null,
      revision: null,
      isInitSentry: false
    };

    this.spinnerNode = document.getElementById('preloaderScreen');
  }

  checkAuth = (resolve) => {
    fetchRequest({
      url: '/check-auth',
      sccb: (data = {}) => {
        const {user, organization_user, reinstall} = data;

        if (reinstall) {
          if (localStorage.getItem('jwt')) {
            localStorage.removeItem('jwt');
            window.location = '/login';
          }
        }

        let page;
        if (!user) {
          page = pages.entrance;
        } else {
          if (!user.email) {
            page = pages.setupEmail;
          } else if (user.isEmailNewVerifiedProcess || user.isEmailCurVerifiedProcess) {
            page = pages.confirmEmail;
          } else if (user.hasInvitation) {
            page = pages.invitation;
          } else if (user.isStaff && !user.onboardingId && !organization_user) {
            page = pages.staff;
          } else if (user.onboardingId) {
            page = pages.setup;
          } else if (user.isOnboardingUserProcess) {
            page = pages.onboarding;
          } else {
            page = pages.app;
          }
        }

        this.setThemeColor(user && user.themeColor);
        this.setModeColor(user && user.colorMode);
        this.spinnerChangeTheme(user && user.colorMode);

        if (this._initApp) {
          this.props.startUpdateAppInfo(data);
        } else if (page === pages.app) {
          this.props.updateUser(user)
        }

        const sentryData = {};
        if (data.user && data.user.organizationId) sentryData.id = data.user.organizationId;
        if (data.user && data.user.organizationName) sentryData.name = data.user.organizationName;
        if (data.user && data.user.email) sentryData.email = data.user.email;

        resolve({
          sentryData,
          lang: data.user && data.user.lang,
          page
        });
      }
    })
  };

  getEnvironment = (page, lang, sentryData, resolve) => {
    if (!__ENV__.notFound) {
      resolve({page, lang, configs: __ENV__, sentryData})
    } else {
      fetchRequest({
        url: '/settings/env',
        sccb: (configs) => resolve({page, lang, configs, sentryData})
      });
    }
  }

  initState = (page, revision, SENTRY_DSN, SENTRY_RATE, sentryData) => {
    const generateState = {
      page,
      revision,
      isInitSentry: true
    };

    if (analyticsEnabled && !this.state.isInitSentry && SENTRY_DSN) {
      Sentry.init({
        release: revision,
        dsn: SENTRY_DSN,
        tracesSampleRate: SENTRY_RATE,
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ],
      });


      if (!isEmpty(sentryData)) {
        Sentry.setUser(sentryData);
      }
    }

    this.setState(generateState);
    this.props.finishUpdateAppInfo();
  };

  getQueryLang = (env) => {
    const lang = querystring.parse(document.location.search.substring(1)).lang
    return lang && !!find(env.langs, (i) => i.code === lang) ? lang : null
  }

  initApp = ({page, lang, configs, sentryData}) => {
    configs = applyPublicConfig(configs);
    const {env} = configs;

    const realLang = lang || this.getQueryLang(env) || localStorage.getItem('lang') || 'en';

    return new Promise((resolve) => window.__ ? resolve(true) : setLang(realLang, resolve))
      .then(() => {
        setLocale(realLang);
        localStorage.setItem('lang', realLang);
        document.documentElement.setAttribute('lang', realLang);
        document.body.setAttribute('dir', realLang !== 'ar' ? 'ltr' : 'rtl');
        this.props.updateAppInfo(configs);
        this.initState(page.fileName, env.meta.revision, env.SENTRY_DSN, env.SENTRY_RATE, sentryData);
      });
  };

  updatePage() {
    this.spinnerShow();

    new Promise((resolve) => this.checkAuth(resolve))
      .then(({page, lang, sentryData}) => new Promise((resolve) => this.getEnvironment(page, lang, sentryData, resolve))
        .then((pageAndConfigs) => this.initApp(pageAndConfigs)));
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this._initApp && !this.props.appInfo.loading && nextProps.appInfo.loading) {
      this.updatePage();
    }

    if (this._initApp) this._initApp = false;
  }

  componentDidMount() {
    this._initApp = true;

    if(window.self === window.top) {
      this.updatePage();
    }
  }

  setThemeColor(color) {
    themeColorChanger(color || themeColorGetter());
  }

  setModeColor(mode) {
    modeColorChanger(mode || modeColorGetter());
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(nextState, this.state);
  }

  asyncModule = () => asyncComponent(() => {
    switch (this.state.page) {
      case pages.entrance.fileName: {
        return import('./Entrance');
      }
      case pages.app.fileName: {
        return import('./App');
      }
      case pages.setup.fileName: {
        return import('./Setup');
      }
      case pages.setupEmail.fileName: {
        return import('./SetupEmailWithIntegration');
      }
      case pages.confirmEmail.fileName: {
        return import('./ConfirmEmail');
      }
      case pages.invitation.fileName: {
        return import('./Invitation');
      }
      case pages.onboarding.fileName: {
        return import('./Onboarding');
      }
      default: {
        return import('./Staff');
      }
    }
  });

  spinnerChangeTheme(mode) {
    const isLightMode = mode === 'light';
    this.spinnerNode.style.background = isLightMode ? '#fff' : '#202124';
  }

  spinnerShow() {
    this.spinnerNode.style.display = 'flex';
  }

  spinnerHide = () => {
    this.spinnerNode.style.display = 'none';
  }

  render() {
    if (!this.state.page) return null;
    const Module = this.asyncModule();

    return (
      <DndProvider backend={TouchBackend} options={eventsOptions}>
        <Module key={1} cb={this.spinnerHide} />
      </DndProvider>
    )
  }
}

const mapStateToProps = ({appInfo}) => ({appInfo});

const mapDispatchToProps = (dispatch) => {
  return {
    updateUser: (data) => dispatch(updateUser(data)),
    finishUpdateAppInfo: () => dispatch(finishUpdateAppInfo()),
    startUpdateAppInfo: (data) => dispatch(startUpdateAppInfo(data)),
    updateAppInfo: (data) => dispatch(updateAppInfo(data)),
  }
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(IndexRouter);
